const docData = {
    categories: [
        {
            id: 'getting-started',
            title: 'Getting Started',
            icon: '📚',
            sections: [
                {
                    id: 'introduction',
                    title: 'Introduction',
                    content: `The MIT CSHub Streamlined Pavement Life Cycle Assessment (LCA) and Life Cycle Cost Analysis (LCCA) Tool is designed to help engineers and researchers evaluate the environmental and economic impacts of pavement designs.

This tool allows you to:
- Evaluate and Compare different pavement materials (concrete vs asphalt)
- Analyze Greenhouse Gas (GHG) emissions (CO_2-eq) across the pavement lifecycle
- Evaluate life cycle costs including construction, maintenance, and user costs
- Consider regional climate and cost variations by state`,
                    externalLinks: []
                },
                {
                    id: 'workflow',
                    title: 'Understanding the Workflow',
                    content: `The tool follows a structured workflow:

1. **Pavement Context** - Define climate, cost parameters, and traffic conditions
2. **Embodied Emissions** - Configure material properties for concrete or asphalt
3. **Pavement Design** - Specify layer thicknesses and material properties, as well as the maintenance and rehabilitation (M&R) strategies during the lifecycle of pavement infrastructure.
4. **Results** - Visulization of LCA (GHG emissions) and LCCA (costs) results and probablistic and comparative analyses for alternative design and M&R strategies.

Each step builds upon the previous, conducting a comprehensive LCA and LCCA analysis at the early age for your pavement design optimization or late age for the asset management.`,
                    externalLinks: []
                },
                {
                    id: 'first-experiment',
                    title: 'Creating Your First Experiment',
                    content: `To create your first experiment:

1. Navigate to the **Dashboard** and click "New Experiment"
2. Select a **State** to load regional climate and cost data
3. Configure basic parameters or use "Detailed Traffic Data" for advanced settings
4. Proceed through Embodied Emissions and Pavement Design
5. Run the analysis to view results

Tip: Start with default values to understand the tool before customizing.`,
                    externalLinks: []
                }
            ]
        },
        {
            id: 'pavement-context',
            title: 'Pavement Context',
            icon: '🌡️',
            sections: [
                {
                    id: 'climate-parameters',
                    title: 'Climate Parameters',
                    content: `Climate parameters affect pavement performance and environmental calculations:

- **Annual Precipitation Days**: Number of days with precipitation ≥0.01 inch. Affects pavement deterioration and maintenance scheduling.
- **Precipitation Threshold**: Minimum rainfall intensity (in./hr) triggering surface runoff. Used in drainage calculations.
- **Solar Radiation**: Average daily solar radiation (kWh/m²/day). Influences pavement temperature and aging, as well as albedo impact.

These values are automatically populated when you select a state but can be customized.`,
                    externalLinks: []
                },
                {
                    id: 'cost-parameters',
                    title: 'Cost Parameters',
                    content: `Cost parameters vary by state and affect economic analysis:

- **Asphalt Price ($/cu yd or $/m³)**: Hot mix asphalt cost for construction and maintenance
- **Concrete Price ($/cu yd or $/m³)**: Ready-mix concrete cost
- **Diesel Price ($/gal or $/liter)**: For equipment and truck operating costs
- **Gas Price ($/gal or $/liter)**: For passenger vehicle operating costs
- **Annual Discount Rate (%)**: Converts future costs to present value`,
                    externalLinks: []
                },
                {
                    id: 'traffic-content',
                    title: 'Traffic Content',
                    content: `Traffic parameters define loading conditions:

- **Traffic Volume**: High/Medium/Low based on state AADT percentiles
- **Truck Percentage**: Heavy vehicle proportion affecting pavement loading
- **Traffic Speed**: Affects fuel consumption and vehicle emissions
- **Traffic Growth**: Annual growth rate (0.5-3%)
- **Number of Lanes**: 2, 4, 6, or 8 lanes
- **Pavement Length**: Project length in miles or km
- **Lane/Shoulder Width**: Standard widths in ft or m`,
                    externalLinks: []
                }
            ]
        },
        {
            id: 'embodied-emissions',
            title: 'Embodied Emissions',
            icon: '🧪',
            sections: [
                {
                    id: 'concrete-emissions',
                    title: 'Concrete Emissions (A1-A3)',
                    content: `Concrete embodied emissions include material production phases:

**Material Properties:**
- Portland Cement, Fly Ash, Slag Cement - binder components
- Mixing Water - affects workability
- Coarse/Fine Aggregates - crushed or natural
- Air content and admixtures (water reducers, accelerators)

**Transportation (A2):**
- Emissions from transporting materials to the plant

**Energy (A3):**
- Purchased electricity, natural gas, fuels used in production

The tool calculates total kgCO2eq/m³ based on emission factors.`,
                    externalLinks: []
                },
                {
                    id: 'asphalt-emissions',
                    title: 'Asphalt Emissions',
                    content: `Asphalt embodied emissions consider:

**Material Properties:**
- Bitumen Content (%) - typically 4-11%
- Bitumen Impact - CO2 emissions per unit
- Gravel and Sand Impact - aggregate emissions
- RAP Content (%) - Recycled Asphalt Pavement percentage
- WAP Content (%) - Warm mix additive percentage

**Transportation and Heating:**
- Material transport emissions
- Asphalt heating/production emissions

Higher RAP content reduces virgin material usage and emissions.`,
                    externalLinks: []
                }
            ]
        },
        {
            id: 'pavement-design',
            title: 'Pavement Design',
            icon: '🛣️',
            sections: [
                {
                    id: 'layer-configuration',
                    title: 'Layer Configuration',
                    content: `Pavement structures consist of multiple layers:

**Typical Layers:**
- Surface course (wearing layer) - Asphalt
- Binder course - Asphalt
- Base layer
- Subbase layer

Each layer has:
- **Thickness**: Specified in inches or mm
- **Material Type**: Concrete, asphalt, or aggregate
- **Properties**: Strength, stiffness characteristics

The design affects both initial cost and long-term performance.`,
                    externalLinks: []
                },
                {
                    id: 'design-parameters',
                    title: 'Design Parameters',
                    content: `Key design considerations:

- **Analysis Period**: Typically 20-50 years
- **Initial IRI**: Starting condition (usually 64 in/mile or 1.0 m/km)
- **Reliability**: Probability of meeting performance requirements

Higher reliability requires more conservative (thicker) designs.`,
                    externalLinks: []
                }
            ]
        },
        {
            id: 'results',
            title: 'Results & Reports',
            icon: '📊',
            sections: [
                {
                    id: 'co2-results',
                    title: 'Understanding CO2 Results',
                    content: `CO2 equivalent results show environmental impact:

**Components:**
- Material Production (A1-A3)
- Construction (A4-A5)
- Use Phase - Pavement Vehicle Interaction (PVI), Albeldo impact, Carbonation of cementitious materials.
- Maintenance & Rehabilitation
- End of Life

Results are shown as total kgCO2eq and broken down by phase. Compare designs to identify the most environmentally friendly option.`,
                    externalLinks: []
                },
                {
                    id: 'cost-results',
                    title: 'Cost Analysis',
                    content: `Life cycle cost analysis includes:

**Components:**
- Material Production (A1-A3)
- Construction (A4-A5)
- Use Phase - Pavement Vehicle Interaction (PVI)
- Maintenance & Rehabilitation
- End of Life

Results are presented as Net Present Value (NPV) using the specified discount rate.`,
                    externalLinks: []
                }
            ]
        },
        {
            id: 'unit-systems',
            title: 'Unit Systems',
            icon: '📏',
            sections: [
                {
                    id: 'conversions',
                    title: 'Metric vs Imperial',
                    content: `The tool supports both unit systems:

**Toggle between systems** using the Unit Toggle button on each page.

**Common Conversions:**
| Imperial | Metric |
|----------|--------|
| cu yd | m³ |
| lb/yd³ | kg/m³ |
| gal | liter |
| mile | km |
| ft | m |
| mph | km/h |

Note: Backend calculations always use consistent units regardless of display preference.`,
                    externalLinks: []
                }
            ]
        },
        {
            id: 'resources',
            title: 'External Resources',
            icon: '📁',
            sections: [
                {
                    id: 'documents',
                    title: 'Reference Documents',
                    content: `Access additional documentation and reference materials:`,
                    externalLinks: [
                        { title: 'User Guide (PDF)', url: '#', description: 'Comprehensive user manual' },
                        { title: 'Technical Documentation', url: '#', description: 'Methodology and calculations' },
                        { title: 'Example Projects', url: '#', description: 'Sample analyses and case studies' }
                    ]
                },
                {
                    id: 'support',
                    title: 'Support & Contact',
                    content: `For questions or support:

- Visit the MIT CSHub website @ https://cshub.mit.edu/streamlined-pavement-lca-lcca-tool/
- Contact the development team: haoranli@mit.edu
- Report issues or suggest features: haoranli@mit.edu

This tool is developed and maintained by the MIT Concrete Sustainability Hub.`,
                    externalLinks: []
                }
            ]
        }
    ]
};

export default docData;
